import { useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { styled } from '@mui/material';
import { TextField } from '@calefy-inc/informedMaterial';
import { useFormApi, useFormState } from 'informed';
import { VisuallyHiddenInput } from '../../../../../../components/common/VisuallyHiddenInput';
import {
  WhiteLabelSectionDivider,
  WhiteLabelSectionRoot,
} from './whiteLabelSectionCommon';
import {
  normalizeWhiteLabelColourForMui,
  validateWhiteLabelColourField,
} from '../whiteLabelSettingsFormHelpers';

export interface PaletteSectionProps {
  /** From `whiteLabelPaletteColourRoles` — Django `WhiteLabelPalette` FK order. */
  paletteColourRoles: readonly string[];
  /** From `whiteLabelColourShadeFields` — `WhiteLabelColour` CharField order. */
  colourShadeFields: readonly string[];
}

/** Native colour inputs only accept #rrggbb; expand #rgb shorthand */
function toColourInputValue(hex: string): string {
  const s = hex.trim();
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    const [r, g, b] = [s[1], s[2], s[3]];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) {
    return s.toLowerCase();
  }
  return '#000000';
}

/** Relative luminance (sRGB) — used when contrastText is left empty in the form. */
function fallbackTextOnMainHex(mainHex: string): string {
  const expanded = toColourInputValue(mainHex);
  const m = /^#([0-9a-f]{6})$/.exec(expanded);
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const linearize = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const L =
    0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
  return L > 0.179 ? '#000000' : '#ffffff';
}

/** Resolved CSS colour for MUI-style contrast text on the main brand swatch. */
function effectiveContrastColour(
  mainHex: string,
  contrastInput: string,
): string {
  const t = contrastInput.trim();
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(t)) {
    return toColourInputValue(t);
  }
  const normalized = normalizeWhiteLabelColourForMui(t);
  if (normalized != null) return normalized;
  return fallbackTextOnMainHex(mainHex);
}

/** Small clickable swatch — clicking opens the native colour picker */
function ColourSwatch({ name, value }: { name: string; value: string }) {
  const formApi = useFormApi();
  const isValid = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value);
  const pickerValue = isValid ? toColourInputValue(value) : '#000000';

  return (
    <Tooltip title={value || 'not set'} arrow>
      <SwatchLabelRoot
        htmlFor={`colour-input-${name}`}
        isValidHex={isValid}
        swatchFill={value}
      >
        {/* Visually hidden; label associates — updates the same Informed field as the TextField. */}
        <VisuallyHiddenInput
          id={`colour-input-${name}`}
          type='color'
          value={pickerValue}
          onChange={(e) => {
            formApi.setValue(name, e.target.value);
          }}
        />
      </SwatchLabelRoot>
    </Tooltip>
  );
}

/** One row: swatch + text field for a single shade */
function ShadeRow({ role, shade }: { role: string; shade: string }) {
  const fieldName = `palette_${role}_${shade}`;
  const { values } = useFormState();
  const value = String(values[fieldName] ?? '');

  return (
    <ShadeRowRoot>
      <ColourSwatch name={fieldName} value={value} />
      <ShadeTextField
        name={fieldName}
        label={shade}
        validate={validateWhiteLabelColourField}
        validateOnChange
        variant='standard'
        size='small'
        fullWidth
        placeholder='#000000'
      />
    </ShadeRowRoot>
  );
}

/** Contrast text: same swatch + hex row as shades so users can pick a colour in the browser. */
function ContrastTextPickRow({ role }: { role: string }) {
  const fieldName = `palette_${role}_contrastText`;
  const { values } = useFormState();
  const value = String(values[fieldName] ?? '');

  return (
    <ShadeRowRoot>
      <ColourSwatch name={fieldName} value={value} />
      <ShadeTextField
        name={fieldName}
        label='Contrast text'
        helperText='Text on filled buttons (e.g. #ffffff). Leave empty for auto.'
        validate={validateWhiteLabelColourField}
        validateOnChange
        variant='standard'
        size='small'
        fullWidth
        placeholder='#ffffff'
      />
    </ShadeRowRoot>
  );
}

/** Preview strip — all roles use the same fixed width (1/3 of header flex area) */
function PalettePreviewStrip({
  role,
  colourShadeFields: shadeKeys,
  brandRoleSet,
}: {
  role: string;
  colourShadeFields: readonly string[];
  /** First roles in `paletteColourRoles` from API — simplified preview (single swatch). */
  brandRoleSet: ReadonlySet<string>;
}) {
  const { values } = useFormState();

  if (brandRoleSet.has(role)) {
    const mainKey =
      shadeKeys.find((s) => s === 'main') ?? shadeKeys[0] ?? 'main';
    const val = String(values[`palette_${role}_${mainKey}`] ?? '');
    const isValid = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(val);
    const contrastRaw =
      role === 'primary' || role === 'secondary'
        ? String(values[`palette_${role}_contrastText`] ?? '')
        : '';
    const previewBg = isValid ? toColourInputValue(val) : '';
    const previewFg = isValid
      ? effectiveContrastColour(val, contrastRaw)
      : 'inherit';
    return (
      <PreviewStripRoot>
        <Tooltip title={`${mainKey}: ${val || 'not set'}`} arrow>
          <PreviewSwatchSegment fillIsValid={isValid} fillColour={val} />
        </Tooltip>
        {(role === 'primary' || role === 'secondary') && (
          <Tooltip
            title={
              contrastRaw.trim().length > 0
                ? `contrastText: ${contrastRaw}`
                : 'contrastText: (auto from main)'
            }
            arrow
          >
            <PreviewContrastBadge
              $bg={previewBg}
              $fg={previewFg}
              $dim={!isValid}
            >
              Aa
            </PreviewContrastBadge>
          </Tooltip>
        )}
      </PreviewStripRoot>
    );
  }

  return (
    <PreviewStripRoot>
      {shadeKeys.map((shade) => {
        const val = String(values[`palette_${role}_${shade}`] ?? '');
        const isValid = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(val);
        return (
          <Tooltip key={shade} title={`${shade}: ${val || 'not set'}`} arrow>
            <PreviewSwatchSegment fillIsValid={isValid} fillColour={val} />
          </Tooltip>
        );
      })}
    </PreviewStripRoot>
  );
}

/** Live samples for brand main + contrast (mirrors TypographySection preview panels). */
function BrandColoursLivePreview({
  paletteColourRoles,
  colourShadeFields,
}: {
  paletteColourRoles: readonly string[];
  colourShadeFields: readonly string[];
}) {
  const { values } = useFormState();
  const brandRoles = paletteColourRoles.slice(0, 2);
  if (brandRoles.length === 0) {
    return null;
  }

  const mainKey =
    colourShadeFields.find((s) => s === 'main') ??
    colourShadeFields[0] ??
    'main';

  return (
    <BrandPreviewPanel>
      <BrandPreviewCaption variant='subtitle2'>
        Brand colour preview
      </BrandPreviewCaption>
      <BrandPreviewSamplesRow>
        {brandRoles.map((role) => {
          const mainHex = String(values[`palette_${role}_${mainKey}`] ?? '');
          const contrastRaw =
            role === 'primary' || role === 'secondary'
              ? String(values[`palette_${role}_contrastText`] ?? '')
              : '';
          const validMain = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(mainHex);
          const bg = validMain ? toColourInputValue(mainHex) : '';
          const fg = validMain
            ? effectiveContrastColour(mainHex, contrastRaw)
            : '';
          const label =
            role === 'primary'
              ? 'Primary'
              : role === 'secondary'
              ? 'Secondary'
              : role;
          return (
            <BrandPreviewColumn key={role}>
              <BrandPreviewRoleLabel variant='caption' color='text.secondary'>
                {label} — contained button
              </BrandPreviewRoleLabel>
              <SampleContainedButton $valid={validMain} $bg={bg} $fg={fg}>
                {`Sample ${label}`}
              </SampleContainedButton>
            </BrandPreviewColumn>
          );
        })}
      </BrandPreviewSamplesRow>
    </BrandPreviewPanel>
  );
}

/** Collapsible section for one colour role */
function ColourRoleFields({
  role,
  colourShadeFields,
  brandRoleSet,
}: {
  role: string;
  colourShadeFields: readonly string[];
  brandRoleSet: ReadonlySet<string>;
}) {
  const [open, setOpen] = useState(false);
  const isSimple = brandRoleSet.has(role);
  const mainKey =
    colourShadeFields.find((s) => s === 'main') ??
    colourShadeFields[0] ??
    'main';

  return (
    <RoleCard>
      <RoleHeaderRow onClick={() => setOpen((v) => !v)}>
        <RoleTitle variant='subtitle2'>{role}</RoleTitle>

        <RolePreviewSlot>
          <PalettePreviewStrip
            role={role}
            colourShadeFields={colourShadeFields}
            brandRoleSet={brandRoleSet}
          />
        </RolePreviewSlot>

        <IconButton size='small' tabIndex={-1}>
          {open ? (
            <ExpandLessIcon fontSize='small' />
          ) : (
            <ExpandMoreIcon fontSize='small' />
          )}
        </IconButton>
      </RoleHeaderRow>

      <Collapse in={open}>
        <RoleBody>
          {isSimple ? (
            <RoleSimpleShadeWrap>
              <ShadeRow role={role} shade={mainKey} />
            </RoleSimpleShadeWrap>
          ) : (
            <RoleShadeGrid
              $columnCountSm={Math.min(colourShadeFields.length, 6)}
            >
              {colourShadeFields.map((shade) => (
                <ShadeRow key={shade} role={role} shade={shade} />
              ))}
            </RoleShadeGrid>
          )}
          {(role === 'primary' || role === 'secondary') && (
            <ContrastTextFieldRow>
              <ContrastTextPickRow role={role} />
            </ContrastTextFieldRow>
          )}
        </RoleBody>
      </Collapse>
    </RoleCard>
  );
}

export function PaletteSection({
  paletteColourRoles,
  colourShadeFields,
}: PaletteSectionProps) {
  if (paletteColourRoles.length === 0 || colourShadeFields.length === 0) {
    return null;
  }

  // Same source as layout: first N roles from API order get compact brand-row UX.
  const brandRoles = paletteColourRoles.slice(0, 2);
  const brandRoleSet = new Set(brandRoles);
  const semanticRoles = paletteColourRoles.slice(2);

  return (
    <WhiteLabelSectionRoot>
      <Typography variant='h6' gutterBottom>
        Theme colours
      </Typography>
      <WhiteLabelSectionDivider />

      <BrandRolesGrid>
        {brandRoles.map((role) => (
          <ColourRoleFields
            key={role}
            role={role}
            colourShadeFields={colourShadeFields}
            brandRoleSet={brandRoleSet}
          />
        ))}
      </BrandRolesGrid>

      <BrandColoursLivePreview
        paletteColourRoles={paletteColourRoles}
        colourShadeFields={colourShadeFields}
      />
      <SemanticRolesGrid>
        {semanticRoles.map((role) => (
          <ColourRoleFields
            key={role}
            role={role}
            colourShadeFields={colourShadeFields}
            brandRoleSet={brandRoleSet}
          />
        ))}
      </SemanticRolesGrid>
    </WhiteLabelSectionRoot>
  );
}

// --- Section-specific layout ---

const RoleGridBase = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: '1fr',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}));

const BrandPreviewPanel = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.action.hover,
  border: '1px solid',
  borderColor: theme.palette.divider,
}));

const BrandPreviewCaption = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  color: theme.palette.text.secondary,
  fontWeight: 500,
}));

const BrandPreviewSamplesRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const BrandPreviewColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(0.75),
  minWidth: 160,
}));

const BrandPreviewRoleLabel = styled(Typography)({
  textTransform: 'capitalize',
});

/** MUI-like contained button — uses form main/contrast only (no theme palette). */
const SampleContainedButton = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== '$valid' && prop !== '$bg' && prop !== '$fg',
})<{ $valid: boolean; $bg: string; $fg: string }>(
  ({ theme, $valid, $bg, $fg }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.5,
    textTransform: 'none',
    boxSizing: 'border-box',
    ...($valid
      ? {
          backgroundColor: $bg,
          color: $fg,
          border: '1px solid transparent',
        }
      : {
          backgroundColor: theme.palette.grey[300],
          color: theme.palette.text.disabled,
          border: `1px solid ${theme.palette.divider}`,
        }),
  }),
);

const BrandRolesGrid = styled(RoleGridBase)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const SemanticRolesGrid = styled(RoleGridBase)({});

const SwatchLabelRoot = styled('label', {
  shouldForwardProp: (prop) => prop !== 'isValidHex' && prop !== 'swatchFill',
})<{ isValidHex: boolean; swatchFill: string }>(
  ({ theme, isValidHex, swatchFill }) => ({
    display: 'block',
    width: 28,
    height: 28,
    borderRadius: theme.shape.borderRadius,
    border: '1px solid',
    borderColor: theme.palette.divider,
    backgroundColor: isValidHex ? swatchFill : theme.palette.background.default,
    cursor: 'pointer',
    flexShrink: 0,
    '&:hover': { opacity: 0.85 },
  }),
);

const ShadeRowRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: theme.spacing(1),
}));

const ShadeTextField = styled(TextField)({
  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
  '& .MuiInputBase-input': { fontSize: '0.75rem' },
});

const PreviewStripRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  width: '100%',
}));

const PreviewSwatchSegment = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fillIsValid' && prop !== 'fillColour',
})<{ fillIsValid: boolean; fillColour: string }>(
  ({ theme, fillIsValid, fillColour }) => ({
    flex: 1,
    height: 16,
    borderRadius: theme.spacing(0.5),
    border: '1px solid',
    borderColor: theme.palette.divider,
    backgroundColor: fillIsValid ? fillColour : theme.palette.grey[200],
  }),
);

const PreviewContrastBadge = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== '$bg' && prop !== '$fg' && prop !== '$dim',
})<{ $bg: string; $fg: string; $dim: boolean }>(
  ({ theme, $bg, $fg, $dim }) => ({
    flexShrink: 0,
    minWidth: 40,
    height: 16,
    borderRadius: theme.spacing(0.5),
    border: '1px solid',
    borderColor: theme.palette.divider,
    backgroundColor: $dim ? theme.palette.grey[200] : $bg,
    color: $dim ? theme.palette.text.disabled : $fg,
    fontSize: '0.65rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  }),
);

const RoleCard = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
}));

const RoleHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  cursor: 'pointer',
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

const RoleTitle = styled(Typography)({
  textTransform: 'capitalize',
  flex: 1,
});

const RolePreviewSlot = styled(Box)(({ theme }) => ({
  flex: 4,
  minWidth: 0,
  marginRight: theme.spacing(2),
}));

const RoleBody = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const RoleSimpleShadeWrap = styled(Box)({
  maxWidth: 200,
});

const ContrastTextFieldRow = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  maxWidth: 320,
}));

/** `$columnCountSm`: transient prop — not forwarded to DOM. */
const RoleShadeGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$columnCountSm',
})<{ $columnCountSm: number }>(({ theme, $columnCountSm }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(2, 1fr)',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: `repeat(${$columnCountSm}, 1fr)`,
  },
}));
